// ============================================
// MODO LEVANTAMIENTO — Asistente IA que escucha
// juntas/recorridos y extrae requerimientos
// ============================================

import React, { useState, useRef, useCallback } from 'react';
import { API_BASE } from '../config/api';

const PP = "'Poppins', sans-serif";
const colores = {
  principal: '#5c9adb',
  oscuro: '#2f5f9e',
  verde: '#5cd4b0',
  rojo: '#e05c5c',
  amarillo: '#dcde74',
  fondo: '#f3f4f4',
  gris: '#4e5357',
  grisClaro: '#5e666e',
};

const TIPOS_INFO = {
  correctivo: { icono: '🔧', label: 'Correctivo', color: '#e05c5c', bg: '#fff0f0' },
  mantenimiento: { icono: '🔩', label: 'Mantenimiento', color: '#f4a261', bg: '#fff8f0' },
  proyecto: { icono: '📐', label: 'Proyecto', color: '#5c9adb', bg: '#eef5ff' },
  venta: { icono: '💼', label: 'Venta', color: '#5cd4b0', bg: '#f0faf6' },
  adicional: { icono: '🔄', label: 'Adicional', color: '#a78bfa', bg: '#f5f0ff' },
  otro: { icono: '📋', label: 'Otro', color: '#8fb8e8', bg: '#f0f5fa' },
};

const URGENCIAS_INFO = {
  normal: { label: 'Normal', color: '#5cd4b0', bg: '#f0faf6' },
  urgente: { label: 'Urgente', color: '#f4a261', bg: '#fff8f0' },
  critico: { label: 'Crítico', color: '#e05c5c', bg: '#fff0f0' },
};

const BTN = { border: 'none', borderRadius: '12px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, transition: 'opacity 0.15s' };

function ModoLevantamiento({ abierto, onCerrar, onCrear, esMovil }) {
  // Estados del flujo
  const [fase, setFase] = useState('inicio'); // inicio, grabando, pausado, texto, procesando, resultados
  const [transcripcion, setTranscripcion] = useState('');
  const [interimText, setInterimText] = useState('');
  const [resultados, setResultados] = useState([]);
  const [seleccionados, setSeleccionados] = useState({});
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);

  // ============================================
  // VOZ — Web Speech API
  // ============================================
  const iniciarGrabacion = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError('Tu navegador no soporta reconocimiento de voz. Escribe el texto manualmente.');
      setFase('texto');
      return;
    }
    const recognition = new SR();
    recognition.lang = 'es-MX';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (e) => {
      let final = '';
      let interim = '';
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          final += t + ' ';
        } else {
          interim += t;
        }
      }
      if (final) {
        setTranscripcion(prev => prev + final);
        setInterimText('');
      } else {
        setInterimText(interim);
      }
    };

    recognition.onerror = (e) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        setError('Error de reconocimiento: ' + e.error);
      }
    };

    recognition.onend = () => {
      // Si estamos en modo grabando (no pausado), reiniciar
      if (recognitionRef.current && recognitionRef.current._activo) {
        try { recognition.start(); } catch (_) { /* ignore */ }
      }
    };

    recognition._activo = true;
    recognitionRef.current = recognition;
    recognition.start();
    setFase('grabando');
    setError('');
  }, []);

  const pausarGrabacion = () => {
    if (recognitionRef.current) {
      recognitionRef.current._activo = false;
      recognitionRef.current.stop();
    }
    setInterimText('');
    setFase('pausado');
  };

  const continuarGrabacion = () => {
    iniciarGrabacion();
  };

  const detenerGrabacion = () => {
    if (recognitionRef.current) {
      recognitionRef.current._activo = false;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setInterimText('');
    setFase('texto');
  };

  // ============================================
  // IA — Procesar con Claude API
  // ============================================
  const procesarConIA = async () => {
    const texto = transcripcion.trim();
    if (!texto) return;
    setFase('procesando');
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/analizar-levantamiento`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ transcripcion: texto }),
      });
      const resultado = await response.json();
      if (resultado.error) throw new Error(resultado.error);
      const reqs = resultado.requerimientos || [];

      if (reqs.length === 0) {
        setError('No se detectaron requerimientos en la transcripcion. Intenta con mas detalle.');
        setFase('texto');
        return;
      }

      // Normalizar tipos/urgencias
      const reqsNormalizados = reqs.map((r, i) => ({
        id: i,
        tipo: TIPOS_INFO[r.tipo] ? r.tipo : 'otro',
        urgencia: URGENCIAS_INFO[r.urgencia] ? r.urgencia : 'normal',
        descripcion: r.descripcion || '',
        equipos: r.equipos || '',
      }));

      setResultados(reqsNormalizados);
      const sel = {};
      reqsNormalizados.forEach(r => { sel[r.id] = true; });
      setSeleccionados(sel);
      setFase('resultados');
    } catch (err) {
      setError(err.message);
      setFase('texto');
    }
  };

  // ============================================
  // ACCIONES DE RESULTADOS
  // ============================================
  const toggleSeleccion = (id) => {
    setSeleccionados(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleTodos = () => {
    const todosSeleccionados = resultados.every(r => seleccionados[r.id]);
    const nuevo = {};
    resultados.forEach(r => { nuevo[r.id] = !todosSeleccionados; });
    setSeleccionados(nuevo);
  };

  const actualizarResultado = (id, campo, valor) => {
    setResultados(prev => prev.map(r => r.id === id ? { ...r, [campo]: valor } : r));
  };

  const crearSeleccionados = () => {
    const paraCrear = resultados.filter(r => seleccionados[r.id]);
    if (paraCrear.length === 0) return;
    if (onCrear) onCrear(paraCrear);
    resetear();
  };

  const resetear = () => {
    if (recognitionRef.current) {
      recognitionRef.current._activo = false;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setFase('inicio');
    setTranscripcion('');
    setInterimText('');
    setResultados([]);
    setSeleccionados({});
    setEditando(null);
    setError('');
    if (onCerrar) onCerrar();
  };

  const cantidadSeleccionados = resultados.filter(r => seleccionados[r.id]).length;

  if (!abierto) return null;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div onClick={resetear} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: esMovil ? 'flex-end' : 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: esMovil ? '20px 20px 0 0' : '20px',
        width: esMovil ? '100%' : '600px', maxHeight: esMovil ? '94vh' : '85vh',
        overflowY: 'auto', padding: esMovil ? '20px 16px 28px' : '28px',
        overscrollBehavior: 'contain',
      }}>
        <style>{`
          @keyframes pulseRec { 0%, 100% { box-shadow: 0 0 0 0 rgba(224,92,92,0.4); } 50% { box-shadow: 0 0 0 12px rgba(224,92,92,0); } }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes dots { 0%, 20% { content: '.'; } 40% { content: '..'; } 60%, 100% { content: '...'; } }
        `}</style>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '22px' }}>{'🎙️'}</span>
            <div style={{ fontSize: '18px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>Modo Levantamiento</div>
          </div>
          <div onClick={resetear} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: colores.grisClaro }}>x</div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: '12px 14px', background: '#fff0f0', borderRadius: '10px', marginBottom: '16px', fontSize: '13px', color: colores.rojo, fontFamily: PP, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ flexShrink: 0 }}>{'⚠️'}</span>
            <span>{error}</span>
          </div>
        )}

        {/* ============================================
            FASE: INICIO
            ============================================ */}
        {fase === 'inicio' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'🎙️'}</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '8px' }}>
              Graba tu junta o recorrido
            </div>
            <div style={{ fontSize: '13px', color: colores.grisClaro, fontFamily: PP, lineHeight: 1.6, marginBottom: '28px', maxWidth: '380px', margin: '0 auto 28px' }}>
              Activa el microfono y habla normalmente. La IA detectara automaticamente los requerimientos, problemas y pendientes mencionados.
            </div>

            <button onClick={iniciarGrabacion}
              style={{ ...BTN, background: colores.principal, color: '#fff', fontSize: '16px', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: '10px', margin: '0 auto' }}>
              <span style={{ fontSize: '20px' }}>{'🎤'}</span>
              Iniciar grabacion
            </button>

            <div style={{ marginTop: '16px' }}>
              <span onClick={() => setFase('texto')} style={{ fontSize: '13px', color: colores.principal, fontFamily: PP, cursor: 'pointer', fontWeight: '500' }}>
                O escribe el texto manualmente
              </span>
            </div>
          </div>
        )}

        {/* ============================================
            FASE: GRABANDO / PAUSADO
            ============================================ */}
        {(fase === 'grabando' || fase === 'pausado') && (
          <div>
            {/* Indicador de grabación */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '50%',
                background: fase === 'grabando' ? colores.rojo : '#d0d0d0',
                animation: fase === 'grabando' ? 'pulseRec 1.5s infinite' : 'none',
              }} />
              <span style={{ fontSize: '15px', fontWeight: '700', color: fase === 'grabando' ? colores.rojo : colores.grisClaro, fontFamily: PP }}>
                {fase === 'grabando' ? 'Escuchando...' : 'Pausado'}
              </span>
            </div>

            {/* Texto transcrito */}
            <div style={{
              minHeight: '150px', maxHeight: '300px', overflowY: 'auto',
              padding: '16px', background: '#f9fafb', borderRadius: '14px',
              border: fase === 'grabando' ? `2px solid ${colores.rojo}30` : '2px solid #e0e0e0',
              marginBottom: '16px', fontSize: '14px', color: colores.gris, fontFamily: PP, lineHeight: 1.7,
            }}>
              {transcripcion || <span style={{ color: colores.grisClaro, fontStyle: 'italic' }}>El texto aparecera aqui mientras hablas...</span>}
              {interimText && <span style={{ color: colores.principal, opacity: 0.6 }}>{interimText}</span>}
            </div>

            {/* Controles */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {fase === 'grabando' ? (
                <button onClick={pausarGrabacion} style={{ ...BTN, background: colores.amarillo, color: colores.gris, flex: 1 }}>
                  {'⏸️'} Pausar
                </button>
              ) : (
                <button onClick={continuarGrabacion} style={{ ...BTN, background: colores.verde, color: '#fff', flex: 1 }}>
                  {'🎤'} Continuar
                </button>
              )}
              <button onClick={detenerGrabacion}
                style={{ ...BTN, background: colores.principal, color: '#fff', flex: 2 }}>
                {'✅'} Listo — analizar
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            FASE: TEXTO (edición manual)
            ============================================ */}
        {fase === 'texto' && (
          <div>
            <div style={{ fontSize: '13px', color: colores.grisClaro, fontFamily: PP, marginBottom: '10px' }}>
              Revisa o edita la transcripcion antes de analizar:
            </div>
            <textarea
              ref={textareaRef}
              style={{
                width: '100%', minHeight: '180px', padding: '16px', borderRadius: '14px',
                border: '2px solid #e0e0e0', fontSize: '14px', fontFamily: PP,
                color: colores.gris, lineHeight: 1.7, resize: 'vertical', outline: 'none',
                boxSizing: 'border-box',
              }}
              placeholder="Escribe o pega la transcripcion de la junta..."
              value={transcripcion}
              onChange={e => setTranscripcion(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => { setFase('inicio'); setTranscripcion(''); }} style={{ ...BTN, background: '#f3f4f4', color: colores.gris, flex: 1 }}>
                Cancelar
              </button>
              <button onClick={iniciarGrabacion} style={{ ...BTN, background: colores.rojo + '15', color: colores.rojo, padding: '12px 16px' }}>
                {'🎤'}
              </button>
              <button onClick={procesarConIA} disabled={!transcripcion.trim()}
                style={{ ...BTN, background: transcripcion.trim() ? colores.principal : '#d0d0d0', color: '#fff', flex: 2 }}>
                {'✨'} Analizar con IA
              </button>
            </div>
          </div>
        )}

        {/* ============================================
            FASE: PROCESANDO
            ============================================ */}
        {fase === 'procesando' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ width: '48px', height: '48px', border: `4px solid ${colores.fondo}`, borderTopColor: colores.principal, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: colores.gris, fontFamily: PP, marginBottom: '8px' }}>
              Analizando transcripcion...
            </div>
            <div style={{ fontSize: '13px', color: colores.grisClaro, fontFamily: PP }}>
              La IA esta identificando requerimientos, problemas y pendientes
            </div>
          </div>
        )}

        {/* ============================================
            FASE: RESULTADOS
            ============================================ */}
        {fase === 'resultados' && (
          <div>
            {/* Resumen */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: colores.gris, fontFamily: PP }}>
                  {resultados.length} requerimiento{resultados.length !== 1 ? 's' : ''} detectado{resultados.length !== 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '12px', color: colores.grisClaro, fontFamily: PP }}>{cantidadSeleccionados} seleccionados para crear</div>
              </div>
              <span onClick={toggleTodos} style={{ fontSize: '12px', color: colores.principal, fontFamily: PP, cursor: 'pointer', fontWeight: '600' }}>
                {resultados.every(r => seleccionados[r.id]) ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </span>
            </div>

            {/* Lista de resultados */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {resultados.map(r => {
                const tipoInfo = TIPOS_INFO[r.tipo] || TIPOS_INFO.otro;
                const urgInfo = URGENCIAS_INFO[r.urgencia] || URGENCIAS_INFO.normal;
                const sel = seleccionados[r.id];
                const isEditando = editando === r.id;

                return (
                  <div key={r.id} style={{
                    padding: '14px', borderRadius: '14px',
                    background: sel ? '#fff' : '#fafafa',
                    border: sel ? `2px solid ${tipoInfo.color}30` : '2px solid #f0f0f0',
                    opacity: sel ? 1 : 0.5,
                    transition: 'all 0.15s',
                  }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      {/* Checkbox */}
                      <div onClick={() => toggleSeleccion(r.id)} style={{
                        width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0, marginTop: '2px',
                        border: sel ? `2px solid ${colores.principal}` : '2px solid #d0d0d0',
                        background: sel ? colores.principal : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontSize: '12px', color: '#fff', transition: 'all 0.15s',
                      }}>
                        {sel && '✓'}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Badges */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', fontFamily: PP, background: tipoInfo.bg, color: tipoInfo.color }}>
                            {tipoInfo.icono} {tipoInfo.label}
                          </span>
                          <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', fontFamily: PP, background: urgInfo.bg, color: urgInfo.color }}>
                            {urgInfo.label}
                          </span>
                        </div>

                        {isEditando ? (
                          /* Modo edición */
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <textarea
                              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '13px', fontFamily: PP, resize: 'vertical', minHeight: '60px', outline: 'none', boxSizing: 'border-box' }}
                              value={r.descripcion}
                              onChange={e => actualizarResultado(r.id, 'descripcion', e.target.value)}
                            />
                            {/* Selector tipo */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {Object.entries(TIPOS_INFO).map(([key, t]) => (
                                <span key={key} onClick={() => actualizarResultado(r.id, 'tipo', key)}
                                  style={{
                                    padding: '4px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', fontFamily: PP, cursor: 'pointer',
                                    background: r.tipo === key ? t.color : '#f3f4f4',
                                    color: r.tipo === key ? '#fff' : colores.grisClaro,
                                  }}>{t.icono} {t.label}</span>
                              ))}
                            </div>
                            {/* Selector urgencia */}
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {Object.entries(URGENCIAS_INFO).map(([key, u]) => (
                                <span key={key} onClick={() => actualizarResultado(r.id, 'urgencia', key)}
                                  style={{
                                    padding: '4px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', fontFamily: PP, cursor: 'pointer',
                                    background: r.urgencia === key ? u.color : '#f3f4f4',
                                    color: r.urgencia === key ? '#fff' : colores.grisClaro,
                                  }}>{u.label}</span>
                              ))}
                            </div>
                            <button onClick={() => setEditando(null)}
                              style={{ ...BTN, background: colores.verde, color: '#fff', padding: '8px 16px', fontSize: '12px', alignSelf: 'flex-start' }}>
                              {'✓'} Listo
                            </button>
                          </div>
                        ) : (
                          /* Vista normal */
                          <>
                            <div style={{ fontSize: '13px', color: colores.gris, fontFamily: PP, lineHeight: 1.5 }}>{r.descripcion}</div>
                            {r.equipos && (
                              <div style={{ fontSize: '11px', color: colores.grisClaro, fontFamily: PP, marginTop: '4px' }}>
                                {'📦'} {r.equipos}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Botón editar */}
                      {!isEditando && (
                        <span onClick={() => setEditando(r.id)} style={{ fontSize: '16px', cursor: 'pointer', flexShrink: 0, padding: '2px' }}>
                          {'✏️'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Acciones finales */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setFase('texto'); setResultados([]); }}
                style={{ ...BTN, background: '#f3f4f4', color: colores.gris, flex: 1 }}>
                Volver a editar
              </button>
              <button onClick={crearSeleccionados} disabled={cantidadSeleccionados === 0}
                style={{ ...BTN, background: cantidadSeleccionados > 0 ? colores.principal : '#d0d0d0', color: '#fff', flex: 2, fontSize: '15px' }}>
                {'✓'} Crear {cantidadSeleccionados} requerimiento{cantidadSeleccionados !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ModoLevantamiento;
